import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../../services/api';
import ReportContent from '../ReportContent';
import { isPending } from '../../services/apiUtils';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import WarningSnackbar from '../../components/WarningSnackbar.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../authentication/AuthContext';
import { pollResponses, payCredit, refundCredit } from './ResponsesUtils.js';
import RenderResponses from './renderResponses';
import { mapServerResponsesToLocal } from '../../services/apiUtils';


function Step3Responses({ userData, setUserData, onTopicUpdate, setTabIndex, progress, setProgress, readOnly }) {

  const navigate = useNavigate();
  const { isAdmin, updateUserAttributes } = useAuth();
  const [loading, setLoading] = useState(userData.responsesStatus === 'PENDING' || userData.isGenerating);
  const [error, setError] = useState(null);
  const [pollingFailed, setPollingFailed] = useState(false);
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const models = [
    { id: 'ft:gpt-4.1-nano-2025-04-14:instantflows-b-v:cob2020-data:C1a8jcK6', label: 'Fine tuned OpenAI model' },
    { id: 'gpt-4.1-nano-2025-04-14', label: 'OpenAI gpt 4.1 mini model' },
    { id: 'claude-sonnet-4-20250514', label: 'Anthropic Claude Sonnet model' },
    { id: 'rag_model', label: 'RAG model searching for similar opinion data' },
  ];

  const [selectedModel, setSelectedModel] = useState(models[0].id);

  const generatedModelLabel = (() => {
    const modelVal = (userData && (userData.llm_model || userData.selectedModel)) || selectedModel;
    if (!modelVal) return null;
    const found = models.find(m => m.id === modelVal);
    return found ? found.label : modelVal;
  })();

  useEffect(() => {
    console.log("UPDATE ANIMATED PROGRESS, LOADINGPROGRESS IS", loadingProgress);
    if ((userData.responsesStatus === 'PENDING' && !userData.surveyResponses) || loading) {
      const interval = setInterval(() => {
        setAnimatedProgress(prev => {
          let speed = 0.005;
          if (prev < 10) {
            speed = 0.1;
          } else if (prev < 20) {
            speed = 0.05;
          }
          return Math.min(100, Math.max(speed + prev, loadingProgress));
        });
      }, 50);
      return () => clearInterval(interval);
    } else {
      setAnimatedProgress(0);
    }
  }, [userData.responsesStatus, loading, loadingProgress, userData.surveyResponses]);

  useEffect(() => {
    // Disable polling in readOnly mode
    if (readOnly) return;

    if ((userData.responsesStatus === 'PENDING' || userData.isGenerating) && !pollingFailed) {
      const intervalId = setInterval(async () => {
        try {
          const research = await api.getPreviousResearch(userData.userID, userData.researchID);
          
          const responses = await api.getExistingResponses(userData.userID, userData.researchID);
          const progress = Math.min((responses.iter / Object.keys(userData.selectedPersonas).length) * 100, 100);

          setLoadingProgress(progress);
          if (progress >= 100 || research.responsesStatus == "COMPLETED") {
            const localResponses = mapServerResponsesToLocal(responses.responses, userData.researchID);
            setUserData(prev => ({
              ...prev,
              surveyResponses: localResponses,
              responsesStatus: "COMPLETED",
              isGenerating: false
            }));
            setLoading(false);
          }
        } catch (error) {
          console.error("Error checking research status:", error);
        }
      }, 10000); // Check every 10 seconds
      
      return () => clearInterval(intervalId);
    }
  }, [userData.responsesStatus, userData.isGenerating, loading, pollingFailed, readOnly, userData.userID, userData.researchID, userData.selectedPersonas]);

  useEffect(() => {
    if (
      !loading &&
      userData &&
      Array.isArray(userData.surveyResponses) &&
      userData.surveyResponses.length > 0 &&
      (userData.responsesStatus === 'COMPLETED' || userData.responsesStatus === 'SURVEY_CHANGED')
    ) {
      if (progress < 4) setProgress(4);
    }
  }, [loading, userData.surveyResponses, userData.responsesStatus, progress, setProgress]);

  const handleContinue = async () => {
    if (readOnly) return;
    setTabIndex(3);
    if (progress < 4) {
      setProgress(4);
    }
  };

  const handleGenerateResponses = async () => {
    if (readOnly) return;
    setLoading(true);
    if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
      setLoading(false);
      return;
    }

    setError(null);
    setPollingFailed(false);
    let creditUsed = false;
    try {
      if (!isAdmin) {
        creditUsed = await payCredit(updateUserAttributes, setError, navigate, setLoading);
      }

      await api.generateResponses(
        userData.userID,
        userData.researchID,
        selectedModel
      );

      const newUserData = {
        ...userData,
        responsesStatus: 'PENDING',
        isGenerating: true,  // Add this flag
        lastModified: Date.now() / 1000,
        selectedModel: selectedModel,
      }

      setUserData(newUserData);

      if (onTopicUpdate) {
        onTopicUpdate(newUserData);
      }

    } catch (err) {
      console.log("ERROR WHEN GENERATING RESPONSES", err);
      if (creditUsed) {
        const refundSuccess = await refundCredit(updateUserAttributes);
        if (refundSuccess) {
          setError(`Error when generating responses, your credit has been refunded`);
        } else {
          setError(`Failed generating responses, your credit was not refunded`);
        }
      }
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box data-tabpanel="responses">
        <Typography variant="h5" gutterBottom>
          {userData && Array.isArray(userData.surveyResponses) && userData.surveyResponses.length > 0
            ? `Generated Responses: (${generatedModelLabel || 'Fine tuned OpenAI model'})`
            : (readOnly ? 'Responses' : 'Generate Responses')}
        </Typography>

        {/* Hide Model Choice dropdown in ReadOnly mode */}
        {!readOnly && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 2 }}>
          <Typography variant="subtitle2">Model Choice</Typography>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="model-choice-label">Select model</InputLabel>
            <Select
              labelId="model-choice-label"
              id="model-choice"
              value={selectedModel}
              label="Select model"
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {models.map(m => (
                <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        )}

        <Paper sx={{ p: 3, mt: 2 }}>
          {/* Hide "Generate Now" alert in ReadOnly mode */}
          {!readOnly && (userData.responsesStatus === 'NOT_STARTED' || userData.responsesStatus === null) && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleGenerateResponses}
                  disabled={loading}
                >
                  Generate Now
                </Button>
              }
              sx={{ mb: 2 }}
            >
              Click the button to generate responses from your personas based on your survey.
            </Alert>
          )}
          
          {/* Hide "Regenerate" alert in ReadOnly mode */}
          {!readOnly && (userData.surveyResponses && userData.surveyResponses.length > 0 && userData.responsesStatus === 'SURVEY_CHANGED') && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleGenerateResponses}
                  disabled={loading}
                >
                  Regenerate
                </Button>
              }
              sx={{ mb: 2 }}
            >
              You made changes to your survey questions/context, regenerate responses for complete/accurate results.
            </Alert>
          )}
          <RenderResponses
            userData={userData}
            loading={loading}
            error={error}
            animatedProgress={animatedProgress}
            onGenerateResponses={handleGenerateResponses}
            readOnly={readOnly}
          />
        </Paper>
        {userData.surveyResponses && (userData.responsesStatus === 'COMPLETED' || userData.responsesStatus === 'SURVEY_CHANGED') && !loading && (
          <Box sx={{ mt: 2 }}></Box>
        )}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            Error: {error}
          </Typography>
        )}
        <div style={{ display: 'none' }}>
          <ReportContent userData={userData} />
        </div>
        
        {/* Hide Continue button in ReadOnly mode */}
        {!readOnly && userData.surveyResponses && (userData.responsesStatus === 'COMPLETED' || userData.responsesStatus === 'SURVEY_CHANGED') && (
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{ mt: 2, display: 'block', mx: 'auto' }}
          >
            Continue
          </Button>
        )}
        <WarningSnackbar
          open={openWarningSnackbar}
          message={warningMessage}
          onClose={() => setOpenWarningSnackbar(false)}
        />
      </Box>
    </ThemeProvider>
  );
}

export default Step3Responses;
